import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  DollarSign, 
  Calendar,
  Target,
  Activity,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Edit,
  Plus,
  Filter,
  Download,
  RefreshCw,
  X,
  Search,
  Settings
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import './ProjectDashboardReportsStandalone.css';

const ProjectDashboardReportsStandalone = () => {
  const { success, error: showError } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    // KPI Cards
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalBudget: 0,
    totalSpent: 0,
    averageProgress: 0,
    
    // Project Status Distribution
    statusDistribution: {
      planning: 0,
      active: 0,
      onHold: 0,
      completed: 0,
      cancelled: 0
    },
    
    // Priority Distribution
    priorityDistribution: {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    },
    
    // Budget vs Actual
    budgetData: [],
    
    // Timeline Data
    timelineData: [],
    
    // Team Performance
    teamPerformance: [],
    
    // Recent Activities
    recentActivities: [],
    
    // Upcoming Milestones
    upcomingMilestones: [],
    
    // Risk Analysis
    riskAnalysis: {
      totalRisks: 0,
      highRisks: 0,
      mediumRisks: 0,
      lowRisks: 0
    },
    
    // Project Health
    projectHealth: {
      healthy: 0,
      atRisk: 0,
      critical: 0
    }
  });

  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
    priority: '',
    projectManager: ''
  });

  const [searchTerm, setSearchTerm] = useState('');

  // Load report data
  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/dashboard`, {
        headers: API_CONFIG.HEADERS
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        // Fallback to mock data
        setReportData(getMockReportData());
        showError('Failed to load report data from server. Using sample data.');
      }
    } catch (err) {
      console.error('Error loading report data:', err);
      setReportData(getMockReportData());
      showError('Error loading report data. Using sample data.');
    } finally {
      setLoading(false);
    }
  };

  const getMockReportData = () => ({
    // KPI Cards
    totalProjects: 24,
    activeProjects: 12,
    completedProjects: 8,
    totalBudget: 2500000,
    totalSpent: 1800000,
    averageProgress: 68,
    
    // Project Status Distribution
    statusDistribution: {
      planning: 4,
      active: 12,
      onHold: 2,
      completed: 8,
      cancelled: 1
    },
    
    // Priority Distribution
    priorityDistribution: {
      low: 6,
      medium: 10,
      high: 6,
      critical: 2
    },
    
    // Budget vs Actual
    budgetData: [
      { name: 'Digital Transformation', budget: 500000, actual: 450000, variance: -50000, status: 'On Track', progress: 75 },
      { name: 'Infrastructure Upgrade', budget: 300000, actual: 320000, variance: 20000, status: 'Over Budget', progress: 60 },
      { name: 'Mobile App Development', budget: 200000, actual: 180000, variance: -20000, status: 'On Track', progress: 85 },
      { name: 'Security Enhancement', budget: 150000, actual: 160000, variance: 10000, status: 'Over Budget', progress: 40 },
      { name: 'Process Automation', budget: 100000, actual: 95000, variance: -5000, status: 'On Track', progress: 90 },
      { name: 'Data Analytics Platform', budget: 250000, actual: 200000, variance: -50000, status: 'On Track', progress: 55 },
      { name: 'Cloud Migration', budget: 400000, actual: 420000, variance: 20000, status: 'Over Budget', progress: 30 },
      { name: 'API Development', budget: 180000, actual: 190000, variance: 10000, status: 'Over Budget', progress: 70 }
    ],
    
    // Timeline Data
    timelineData: [
      { month: 'Jan', planned: 5, completed: 3 },
      { month: 'Feb', planned: 8, completed: 6 },
      { month: 'Mar', planned: 12, completed: 10 },
      { month: 'Apr', planned: 15, completed: 12 },
      { month: 'May', planned: 18, completed: 15 },
      { month: 'Jun', planned: 20, completed: 18 }
    ],
    
    // Team Performance
    teamPerformance: [
      { name: 'Sarah Johnson', projects: 3, completion: 95, efficiency: 88, department: 'Engineering' },
      { name: 'Mike Chen', projects: 2, completion: 92, efficiency: 85, department: 'Engineering' },
      { name: 'Lisa Wang', projects: 4, completion: 89, efficiency: 82, department: 'Product' },
      { name: 'David Rodriguez', projects: 2, completion: 94, efficiency: 90, department: 'Engineering' },
      { name: 'Emma Davis', projects: 3, completion: 87, efficiency: 79, department: 'Design' },
      { name: 'Alex Kim', projects: 2, completion: 91, efficiency: 86, department: 'Engineering' },
      { name: 'Tom Wilson', projects: 3, completion: 88, efficiency: 83, department: 'Product' }
    ],
    
    // Recent Activities
    recentActivities: [
      {
        id: 1,
        type: 'project',
        title: 'Digital Transformation Phase 2 Completed',
        description: 'Successfully completed the second phase of digital transformation',
        time: '2 hours ago',
        status: 'success',
        project: 'Digital Transformation'
      },
      {
        id: 2,
        type: 'milestone',
        title: 'Infrastructure Upgrade Milestone',
        description: 'Reached 80% completion milestone',
        time: '4 hours ago',
        status: 'success',
        project: 'Infrastructure Upgrade'
      },
      {
        id: 3,
        type: 'risk',
        title: 'High Risk Identified',
        description: 'Budget overrun risk in Mobile App project',
        time: '6 hours ago',
        status: 'warning',
        project: 'Mobile App Development'
      },
      {
        id: 4,
        type: 'issue',
        title: 'Resource Allocation Issue',
        description: 'Team member unavailability affecting timeline',
        time: '8 hours ago',
        status: 'error',
        project: 'Process Automation'
      },
      {
        id: 5,
        type: 'milestone',
        title: 'API Development Milestone',
        description: 'API endpoints completed and tested',
        time: '10 hours ago',
        status: 'success',
        project: 'API Development'
      }
    ],
    
    // Upcoming Milestones
    upcomingMilestones: [
      {
        id: 1,
        projectName: 'Digital Transformation',
        milestone: 'Phase 3 Kickoff',
        dueDate: '2024-02-15',
        status: 'upcoming',
        assignedTo: 'Sarah Johnson'
      },
      {
        id: 2,
        projectName: 'Mobile App Development',
        milestone: 'Beta Release',
        dueDate: '2024-02-20',
        status: 'upcoming',
        assignedTo: 'Mike Chen'
      },
      {
        id: 3,
        projectName: 'Security Enhancement',
        milestone: 'Penetration Testing',
        dueDate: '2024-02-25',
        status: 'upcoming',
        assignedTo: 'David Rodriguez'
      },
      {
        id: 4,
        projectName: 'Cloud Migration',
        milestone: 'Data Migration',
        dueDate: '2024-03-01',
        status: 'upcoming',
        assignedTo: 'Alex Kim'
      }
    ],
    
    // Risk Analysis
    riskAnalysis: {
      totalRisks: 15,
      highRisks: 3,
      mediumRisks: 5,
      lowRisks: 7
    },
    
    // Project Health
    projectHealth: {
      healthy: 16,
      atRisk: 6,
      critical: 2
    }
  });

  const handleExportReport = (format) => {
    if (format === 'pdf') {
      success('PDF report exported successfully!');
    } else if (format === 'excel') {
      success('Excel report exported successfully!');
    }
  };

  const handleRefreshReport = () => {
    loadReportData();
  };

  const handleApplyFilters = () => {
    // Apply filters logic
    loadReportData();
    success('Filters applied successfully!');
  };

  const handleResetFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      status: '',
      priority: '',
      projectManager: ''
    });
    setSearchTerm('');
    success('Filters reset successfully!');
  };

  const handleCloseWindow = () => {
    window.close();
  };

  if (loading) {
    return (
      <div className="standalone-report">
        <div className="report-header">
          <div className="report-title">Project Dashboard Reports</div>
          <div className="report-actions">
            <button className="btn-export" onClick={() => handleExportReport('pdf')} title="Export Report">
              <Download size={18} />
            </button>
          </div>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="standalone-report">
      {/* Report Header */}
      <div className="report-header">
        <div className="report-title">Project Dashboard Reports</div>
        <div className="report-actions">
          <button className="btn-export" onClick={() => handleExportReport('pdf')} title="Export Report">
            <Download size={18} />
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-group">
            <label className="filter-label">From Date *</label>
            <input
              type="date"
              className="filter-input"
              value={filters.dateFrom}
              onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">To Date *</label>
            <input
              type="date"
              className="filter-input"
              value={filters.dateTo}
              onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              className="filter-select"
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
            >
              <option value="">All Status</option>
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="onHold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Priority</label>
            <select
              className="filter-select"
              value={filters.priority}
              onChange={(e) => setFilters({...filters, priority: e.target.value})}
            >
              <option value="">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Project Manager</label>
            <select
              className="filter-select"
              value={filters.projectManager}
              onChange={(e) => setFilters({...filters, projectManager: e.target.value})}
            >
              <option value="">All Managers</option>
              <option value="sarah">Sarah Johnson</option>
              <option value="mike">Mike Chen</option>
              <option value="lisa">Lisa Wang</option>
              <option value="david">David Rodriguez</option>
            </select>
          </div>
        </div>
        <div className="filter-actions">
          <button className="btn-filter apply" onClick={handleApplyFilters}>
            Apply Filter
          </button>
          <button className="btn-filter reset" onClick={handleResetFilters}>
            Reset Filter
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-container">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search projects, milestones, or activities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Summary Cards - Using Dashboard KPI Card Styling */}
      <div className="summary-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
        <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
          <div className="card-body page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <h3 className="page-kpi-value color-1">{reportData.totalProjects}</h3>
                <p className="page-kpi-label">Total Projects</p>
              </div>
              <div className="page-kpi-icon-container color-1">
                <BarChart3 size={24} className="page-kpi-icon color-1" />
              </div>
            </div>
          </div>
        </div>
        <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
          <div className="card-body page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <h3 className="page-kpi-value color-2">{reportData.activeProjects}</h3>
                <p className="page-kpi-label">Active Projects</p>
              </div>
              <div className="page-kpi-icon-container color-2">
                <Activity size={24} className="page-kpi-icon color-2" />
              </div>
            </div>
          </div>
        </div>
        <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
          <div className="card-body page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <h3 className="page-kpi-value color-3">{reportData.completedProjects}</h3>
                <p className="page-kpi-label">Completed</p>
              </div>
              <div className="page-kpi-icon-container color-3">
                <CheckCircle size={24} className="page-kpi-icon color-3" />
              </div>
            </div>
          </div>
        </div>
        <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
          <div className="card-body page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <h3 className="page-kpi-value color-4">${(reportData.totalBudget / 1000000).toFixed(1)}M</h3>
                <p className="page-kpi-label">Total Budget</p>
              </div>
              <div className="page-kpi-icon-container color-4">
                <DollarSign size={24} className="page-kpi-icon color-4" />
              </div>
            </div>
          </div>
        </div>
        <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
          <div className="card-body page-kpi-body">
            <div className="page-kpi-content">
              <div className="page-kpi-text">
                <h3 className="page-kpi-value color-5">{reportData.averageProgress}%</h3>
                <p className="page-kpi-label">Avg Progress</p>
              </div>
              <div className="page-kpi-icon-container color-5">
                <TrendingUp size={24} className="page-kpi-icon color-5" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table Header */}
      <div className="table-header">
        <div className="table-header-cell">Project Name</div>
        <div className="table-header-cell">Status</div>
        <div className="table-header-cell">Priority</div>
        <div className="table-header-cell">Manager</div>
        <div className="table-header-cell">Budget</div>
        <div className="table-header-cell">Actual</div>
        <div className="table-header-cell">Variance</div>
        <div className="table-header-cell">Progress</div>
        <div className="table-header-cell">Start Date</div>
        <div className="table-header-cell">End Date</div>
        <div className="table-header-cell">Health</div>
        <div className="table-header-cell">Risks</div>
        <div className="table-header-cell">Milestones</div>
        <div className="table-header-cell">Team Size</div>
        <div className="table-header-cell">Actions</div>
      </div>

      {/* Data Table Body */}
      <div className="table-body">
        {reportData.budgetData.map((project, index) => (
          <div key={index} className="table-row">
            <div className="table-cell project-name">
              <div className="project-info">
                <div className="project-title">{project.name}</div>
                <div className="project-code">PRJ-{String(index + 1).padStart(3, '0')}</div>
              </div>
            </div>
            <div className="table-cell">
              <span className={`status-badge ${getStatusClass(project.status)}`}>
                {project.status}
              </span>
            </div>
            <div className="table-cell">
              <span className={`priority-badge ${getPriorityClass(index)}`}>
                {getPriorityLabel(index)}
              </span>
            </div>
            <div className="table-cell">
              {getManagerName(index)}
            </div>
            <div className="table-cell">
              ${project.budget.toLocaleString()}
            </div>
            <div className="table-cell">
              ${project.actual.toLocaleString()}
            </div>
            <div className="table-cell">
              <span className={project.variance >= 0 ? 'variance-over' : 'variance-under'}>
                ${Math.abs(project.variance).toLocaleString()}
                {project.variance >= 0 ? ' Over' : ' Under'}
              </span>
            </div>
            <div className="table-cell">
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${project.progress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{project.progress}%</span>
              </div>
            </div>
            <div className="table-cell">
              {getStartDate(index)}
            </div>
            <div className="table-cell">
              {getEndDate(index)}
            </div>
            <div className="table-cell">
              <span className={`health-badge ${getHealthClass(project.progress, project.variance)}`}>
                {getHealthStatus(project.progress, project.variance)}
              </span>
            </div>
            <div className="table-cell">
              {getRiskCount(index)}
            </div>
            <div className="table-cell">
              {getMilestoneCount(index)}
            </div>
            <div className="table-cell">
              {getTeamSize(index)}
            </div>
            <div className="table-cell">
              <div className="action-buttons">
                <button className="btn-action view" title="View Details">
                  <Eye size={14} />
                </button>
                <button className="btn-action edit" title="Edit Project">
                  <Edit size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Summary */}
      <div className="report-footer">
        <div className="footer-summary">
          <div className="summary-item">
            <span className="summary-label">Total Budget Value</span>
            <span className="summary-value">₹ {reportData.totalBudget.toLocaleString()}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Spent Value</span>
            <span className="summary-value">₹ {reportData.totalSpent.toLocaleString()}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Variance</span>
            <span className="summary-value">₹ {(reportData.totalSpent - reportData.totalBudget).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getStatusClass = (status) => {
  const classes = {
    'On Track': 'status-on-track',
    'Over Budget': 'status-over-budget',
    'At Risk': 'status-at-risk',
    'Critical': 'status-critical'
  };
  return classes[status] || 'status-default';
};

const getPriorityClass = (index) => {
  const priorities = ['priority-low', 'priority-medium', 'priority-high', 'priority-critical'];
  return priorities[index % priorities.length];
};

const getPriorityLabel = (index) => {
  const priorities = ['Low', 'Medium', 'High', 'Critical'];
  return priorities[index % priorities.length];
};

const getManagerName = (index) => {
  const managers = ['Sarah Johnson', 'Mike Chen', 'Lisa Wang', 'David Rodriguez', 'Emma Davis', 'Alex Kim', 'Tom Wilson'];
  return managers[index % managers.length];
};

const getStartDate = (index) => {
  const dates = ['2024-01-15', '2024-02-01', '2024-01-20', '2024-02-10', '2024-01-25', '2024-02-05', '2024-01-30', '2024-02-15'];
  return new Date(dates[index % dates.length]).toLocaleDateString();
};

const getEndDate = (index) => {
  const dates = ['2024-06-30', '2024-08-15', '2024-07-20', '2024-09-10', '2024-08-25', '2024-07-05', '2024-09-30', '2024-08-15'];
  return new Date(dates[index % dates.length]).toLocaleDateString();
};

const getHealthClass = (progress, variance) => {
  if (progress >= 80 && variance <= 0) return 'health-good';
  if (progress >= 60 && variance <= 10000) return 'health-fair';
  if (progress < 60 || variance > 20000) return 'health-poor';
  return 'health-fair';
};

const getHealthStatus = (progress, variance) => {
  if (progress >= 80 && variance <= 0) return 'Healthy';
  if (progress >= 60 && variance <= 10000) return 'Fair';
  if (progress < 60 || variance > 20000) return 'At Risk';
  return 'Fair';
};

const getRiskCount = (index) => {
  const risks = [2, 1, 3, 2, 1, 4, 2, 1];
  return risks[index % risks.length];
};

const getMilestoneCount = (index) => {
  const milestones = [8, 6, 10, 7, 5, 12, 9, 6];
  return milestones[index % milestones.length];
};

const getTeamSize = (index) => {
  const sizes = [5, 3, 7, 4, 2, 8, 6, 3];
  return sizes[index % sizes.length];
};

export default ProjectDashboardReportsStandalone;
